import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { PUT_URL } from '@utils/api/endpoints.ts';
import { UpdateDocumentMutationParams } from '@utils/types/editors/transcripts/api.ts';
import { QueryKeys } from '@utils/api/query-keys.ts';
import { useTranslation } from 'react-i18next';

export function useUpdateSharedDocument() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const extractTokenFromUrl = (): string => {
    const { pathname } = new URL(window.location.href);
    const parts = pathname.split('/').filter(Boolean); // handles trailing slashes
    return parts[parts.length - 1] ?? '';
  };

  return useMutation(
    async (data: UpdateDocumentMutationParams) => {
      const { sessionId, documentId } = data;
      const token = extractTokenFromUrl();

      if (!token) {
        throw new Error(t('token-not-found-in-url'));
      }

      const updateUrl =
        `${PUT_URL(sessionId, documentId).updateSharedSessionDocument}` +
        `?token=${encodeURIComponent(token)}`;

      return Axios.put(updateUrl, data).then(res => res.data);
    },
    {
      onSuccess: (_data, request) => {
        if (request.dontInvalidateQueries) return;

        queryClient.invalidateQueries(
          QueryKeys.document(request.sessionId, request.documentId)
        );

        if (request.folderId) {
          queryClient.invalidateQueries(QueryKeys.folders(request.folderId));
        } else {
          queryClient.invalidateQueries(QueryKeys.folders());
        }

        queryClient.invalidateQueries([
          'transcript-editor-session',
          request.sessionId
        ]);
      }
    }
  );
}
